import React, { useEffect, useState, Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCountryCode } from "./redux/countrySlice";
import { getUserCountry } from "./utils/geoUtils";
import CardSkeleton from "./components/CardSkeleton";
import { trackPageView } from "./utils/analytics";

// Lazy Loaded Pages
const Home = React.lazy(() => import("./pages/Home"));
const Casinos = React.lazy(() => import("./pages/casinos"));
const Bonuses = React.lazy(() => import("./pages/Bonuses"));
const Games = React.lazy(() => import("./pages/Games"));
const Slots = React.lazy(() => import("./pages/slots"));
const Betting = React.lazy(() => import("./pages/betting"));
const AboutUs = React.lazy(() => import("./pages/AboutUs"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const CasinosAdmin = React.lazy(() => import("./pages/CasinosAdmin"));
const BlogsAdmin = React.lazy(() => import("./pages/BlogsAdmin"));
const SettingsAdmin = React.lazy(() => import("./pages/SettingsAdmin"));
const CreateCasino = React.lazy(() => import("./pages/CreateCasino"));
const CreateBlog = React.lazy(() => import("./pages/CreateBlog"));
const EditCasino = React.lazy(() => import("./pages/EditCasino"));
const Subscriber = React.lazy(() => import("./pages/Subscribers"));
const UserLogs = React.lazy(() => import("./pages/UserLogs"));
const EditBlog = React.lazy(() => import("./pages/EditBlog"));
const CasinoDetail = React.lazy(() => import("./pages/CasinoDetail"));
const ThemePage = React.lazy(() => import("./pages/ThemePage"));
const SeoPage = React.lazy(() => import("./pages/SEOPage"));
const Login = React.lazy(() => import("./pages/Login"));
const AdminBanners = React.lazy(() => import("./pages/AdminBanners"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = React.lazy(() => import("./pages/TermsAndCondition"));
const CookiesPolicy = React.lazy(() => import("./pages/CookiesPolicies"));
const ResponsibleGambling = React.lazy(() => import("./pages/ResponsibleGambling"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
function App() {
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchCountry = async () => {
      const data = await getUserCountry();
      if (data?.country_code) {
        dispatch(setCountryCode(data.country_code));
      }
    };

    fetchCountry();
  }, [dispatch]);

  // 2️⃣ Track page view
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return (
    <Suspense fallback={<div className="bg-black100 min-h-screen flex items-center justify-center"><CardSkeleton /></div>}>
      <Routes>
       <Route path="/" element={<Home />} />
      
        <Route path="/casinos" element={<Casinos />} />
        <Route path="/bonuses" element={<Bonuses />} />
        <Route path="/games" element={<Games />} />
        <Route path="/about-us" element={<AboutUs />} />

        <Route path="/login" element={<Login />} />

        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/casinos-admin" element={<CasinosAdmin />} />
        <Route path="/blogs-admin" element={<BlogsAdmin />} />
        <Route path="/ThemePage" element={<ThemePage />} />
        <Route path="/seo-page" element={<SeoPage />} />
        <Route path="/settings-admin" element={<SettingsAdmin />} />
        <Route path="/create-casino" element={<CreateCasino />} />
        <Route path="/create-blog" element={<CreateBlog />} />
        <Route path="/edit-casino/:id" element={<EditCasino />} />
        <Route path="/edit-blog/:id" element={<EditBlog />} />
        <Route path="/casino" element={<CasinoDetail />} />
        <Route path="/casinos/:slug" element={<CasinoDetail />} />
        <Route path="/Subscribers" element={<Subscriber />} />
        <Route path="/UserLogs" element={<UserLogs />} />
        <Route path="/banners" element={<AdminBanners />} />
        

        {/* Filtered Casinos */}
        <Route path="/casinos/crypto" element={<Casinos type="crypto" />} />
        <Route path="/casinos/online" element={<Casinos type="online" />} />
        <Route path="/casinos/certified" element={<Casinos type="certified" />} />
        <Route path="/casinos/mobile" element={<Casinos type="mobile" />} />
        <Route path="/casinos/newest" element={<Casinos type="newest" />} />
       

        {/* Bonus Types */}
        <Route path="/bonuses/latest" element={<Bonuses type="latest" />} />
        <Route path="/bonuses/exclusive" element={<Bonuses type="exclusive" />} />
        <Route path="/bonuses/welcome" element={<Bonuses type="welcome" />} />
        <Route
          path="/bonuses/no-deposit"
          element={<Bonuses type="no-deposit" />}
        />
        <Route
          path="/bonuses/free-spins"
          element={<Bonuses type="freespins" />}
        />
        <Route path="/bonuses/cashback" element={<Bonuses type="cashback" />} />
        <Route
          path="/bonuses/no-wagering"
          element={<Bonuses type="no-wagering" />}
        />

        {/* Games Types */}
        <Route path="/games/casino" element={<Games type="casino" />} />
        <Route path="/games/table" element={<Games type="table" />} />
        <Route path="/games/card" element={<Games type="card" />} />
        <Route path="/games/dice" element={<Games type="dice" />} />
        {/* Slots Types */}
        <Route path="/slots/video" element={<Slots type="video" />} />
        <Route path="/slots/classic" element={<Slots type="classic" />} />
        <Route path="/slots/progressive" element={<Slots type="progressive" />} />
        <Route path="/slots/new" element={<Slots type="new" />} />

        {/* Betting Routes */}
        <Route path="/betting/sports" element={<Betting type="sports" />} />
        <Route path="/betting/new-sites" element={<Betting type="new-sites" />} />
        <Route path="/betting/types" element={<Betting type="types" />} />
        <Route path="/betting/bonuses" element={<Betting type="bonuses" />} />
        <Route path="/betting/free-bets" element={<Betting type="free-bets" />} />

        <Route
          path="/games/real-money-slots" element={<Games type="Real Money Online Slots" />} />
        <Route path="/games/poker" element={<Games type="poker" />} />
        <Route path="/games/bingo" element={<Games type="bingo" />} />
        <Route path="/games/lottery" element={<Games type="lottery" />} />

        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/cookies-policy" element={<CookiesPolicy />} />
        <Route path="/responsible-gambling" element={<ResponsibleGambling />} />

        {/* 404 Catch-All Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default App;
